import React from "react";
import { fmtNumber } from "../shared/format";

// DrainStage — one stage row of the drain (V6 §F.3).
//
// THE MARK IS A 6px SQUARE, 2px GAP, 2px RADIUS (spec F.3, exact). One square per
// conversation — `marks.total` squares on EVERY row, of which `marks.lit` are lit;
// only the lit count changes down the page, so the row length is constant and the
// shrinking is what the eye reads. Above 400 the scale changes and the row says the
// ratio quietly ("1 mark = 5 calls"); it never becomes a bar.
//
// The lit marks are the population still present; the rest carry the `inert`
// treatment (C.2: "dimmed drain marks"). The grid is aria-hidden — 128 focusable
// squares would be a keyboard trap, and the row's own control already carries the
// accessible name "Qualified, 46 conversations".
//
// CLICKABLE ONLY WHEN THERE IS SOMETHING TO OPEN. A stage with no conversation_ids
// selects nothing, so it renders as a plain row with no control and no cursor
// change (spec principle 3 + the "inert panels have no hover affordance" DoD).

const MARK = "h-[6px] w-[6px] rounded-[2px]";

const DrainStage = ({ stage, handle, marks, onStageClick, isActive }) => {
  const label = stage.label || stage.key;
  const count = Number(stage.count ?? 0);
  const ids = Array.isArray(stage.conversation_ids) ? stage.conversation_ids : [];
  const selectable = !!onStageClick && ids.length > 0;

  const body = (
    <>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="gi-body font-medium text-gray-900 dark:text-gray-100">{label}</span>
        <span className="flex items-baseline gap-2">
          {marks.scale > 1 && (
            <span className="gi-label text-gray-500 dark:text-gray-400 normal-case tracking-normal">
              1 mark = {fmtNumber(marks.scale)}
            </span>
          )}
          <span className="gi-body font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
            {fmtNumber(count)}
          </span>
        </span>
      </div>
      {/* CONSTANT LENGTH — `marks.total` is identical on every row; only `lit`
          changes. `gap-[2px]` is the spec's 2px gap. */}
      <div className="flex flex-wrap gap-[2px]" aria-hidden="true">
        {Array.from({ length: marks.total }, (_, i) => (
          <span
            key={i}
            data-lit={i < marks.lit ? "true" : "false"}
            className={`${MARK} ${
              i < marks.lit
                ? "bg-[#32B1FF] dark:bg-[#32B1FF]"
                : "bg-gray-200 dark:bg-gray-700"
            }`}
          />
        ))}
      </div>
    </>
  );

  if (!selectable) {
    return (
      <div data-stage={handle} data-selectable="false" className="px-2 py-2 rounded-lg">
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      data-stage={handle}
      data-selectable="true"
      aria-pressed={isActive}
      onClick={() => onStageClick(stage)}
      className={`w-full text-left px-2 py-2 rounded-lg min-h-[44px] transition-colors ${
        isActive
          ? "bg-[#32B1FF]/10 ring-1 ring-[#32B1FF]/40"
          : "hover:bg-gray-900/5 dark:hover:bg-white/5"
      }`}
    >
      {body}
    </button>
  );
};

export default DrainStage;
